import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { ZepQuizApiError } from '@/api/httpClient';

const useClassroomErrorResolver = (messageByStatus: Record<string, string>) => {
  /** 서버 status 가 매핑되면 로컬라이즈 메시지를, 아니면 fallback 을 반환. */
  const resolve = useCallback(
    (error: unknown, fallback: string): string =>
      error instanceof ZepQuizApiError
        ? messageByStatus[error.status] ?? fallback
        : fallback,
    [messageByStatus],
  );

  return { resolve };
};

/**
 * /join 의 클래스 코드 검증 실패 status 를 common ns 메시지로 변환한다.
 * join 페이지는 SSR 에서 common 만 보장하므로 classroom ns 를 열지 않는다.
 */
export const useJoinClassroomErrorMessages = () => {
  const { t } = useTranslation(['common']);

  const messageByStatus = useMemo<Record<string, string>>(
    () => ({
      CLASS_CODE_INVALID: t('common:classroomJoin.error.classCodeInvalid'),
      CLASS_GROUP_INACTIVE: t('common:classroomJoin.error.classInactive'),
      CLASS_OWNER_CANNOT_JOIN: t('common:classroomJoin.error.ownerCannotJoin'),
      CLASS_MEMBER_ALREADY_JOINED: t(
        'common:classroomJoin.error.alreadyJoined',
      ),
      NOT_FOUND_CLASS_GROUP: t('common:classroomJoin.error.classNotFound'),
    }),
    [t],
  );

  return useClassroomErrorResolver(messageByStatus);
};

/**
 * /classrooms/[classroomId]/join 의 가입 API 실패 status 를 classroom ns 메시지로 변환한다.
 * 서버가 주는 한국어 message 를 그대로 노출하지 않기 위함이다.
 */
export const useClassroomJoinPageErrorMessages = () => {
  const { t } = useTranslation(['classroom', 'common']);

  const messageByStatus = useMemo<Record<string, string>>(
    () => ({
      CLASS_GROUP_INACTIVE: t('common:classroomJoin.error.classInactive'),
      CLASS_OWNER_CANNOT_JOIN: t('common:classroomJoin.error.ownerCannotJoin'),
      CLASS_MEMBER_NAME_REQUIRED: t('classroom:join.error.nameRequired'),
      CLASS_MEMBER_NAME_TOO_LONG: t(
        'classroom:studentManage.error.nameTooLong',
      ),
      CLASS_MEMBER_ALREADY_JOINED: t(
        'common:classroomJoin.error.alreadyJoined',
      ),
      CLASS_STUDENT_LIMIT_EXCEEDED: t(
        'classroom:studentManage.error.studentLimit',
      ),
      NOT_FOUND_CLASS_GROUP: t('classroom:join.error.classroomNotFound'),
    }),
    [t],
  );

  return useClassroomErrorResolver(messageByStatus);
};

/**
 * 학생 관리 화면의 classroom 도메인 API 실패 status 를 classroom ns 메시지로 변환한다.
 * 서버가 주는 한국어 message 를 그대로 노출하지 않기 위함이다.
 */
export const useStudentManageErrorMessages = () => {
  const { t } = useTranslation(['classroom', 'common']);

  const messageByStatus = useMemo<Record<string, string>>(
    () => ({
      CLASS_GROUP_INACTIVE: t('common:classroomJoin.error.classInactive'),
      CLASS_MEMBER_NAME_REQUIRED: t(
        'classroom:studentManage.error.nameRequired',
      ),
      CLASS_MEMBER_NAME_TOO_LONG: t(
        'classroom:studentManage.error.nameTooLong',
      ),
      CLASS_STUDENT_LIMIT_EXCEEDED: t(
        'classroom:studentManage.error.studentLimit',
      ),
      NOT_FOUND_CLASS_GROUP: t('common:classroomJoin.error.classNotFound'),
      NOT_FOUND_CLASS_MEMBER: t('classroom:studentManage.error.memberNotFound'),
      NOT_FOUND_USER: t('classroom:studentManage.error.userNotFound'),
      UNAUTHORIZED: t('classroom:studentManage.error.unauthorized'),
    }),
    [t],
  );

  return useClassroomErrorResolver(messageByStatus);
};
